const EVENT_COPY = {
  certified_video_uploaded: {
    title: "Certified Video Uploaded",
    body: (tournamentName, teamName) =>
      `${teamName || "A team"} uploaded a certified video for ${tournamentName || "this tournament"}.`,
    tone: "info",
  },
  team_assigned_to_tournament: {
    title: "You've Been Entered in a Tournament",
    body: (tournamentName, teamName) =>
      `${teamName || "Your team"} has been registered for ${tournamentName || "a tournament"}.`,
    tone: "info",
  },
  tournament_starting_soon: {
    title: "Tournament starting soon",
    body: (tournamentName) =>
      `${tournamentName || "Your tournament"} starts in 15 minutes. Get checked in and ready to fish.`,
    tone: "notice",
  },
  tournament_ending_soon: {
    title: "Tournament ending soon",
    body: (tournamentName) =>
      `${tournamentName || "Your tournament"} ends in 15 minutes. Make any final submissions now.`,
    tone: "warning",
  },
  tournament_ended: {
    title: "Tournament ended",
    body: (tournamentName) =>
      `${tournamentName || "Your tournament"} has ended and was archived by the tournament director.`,
    tone: "danger",
  },
};

// Backend may emit type strings using a different naming convention. Map them
// to the canonical EVENT_COPY keys above so the client renders consistently.
const EVENT_TYPE_ALIASES = {
  tournament_before_start: "tournament_starting_soon",
  tournament_before_end: "tournament_ending_soon",
  tournament_at_end: "tournament_ended",
  tournament_after_end: "tournament_ended",
};

export function buildTournamentEventNotification(eventData = {}) {
  const rawEventType = String(eventData.type || eventData.eventType || "").trim();
  const eventType = EVENT_TYPE_ALIASES[rawEventType] || rawEventType;
  const template = EVENT_COPY[eventType];
  if (!template) return null;

  const tournamentName = String(
    eventData.tournamentName || eventData.metadata?.tournamentName || ""
  ).trim();
  const teamName = String(eventData.teamName || "").trim();
  const customMessage = String(eventData.message || "").trim();

  // For starting-soon events, compute a target wall-clock time so the UI can
  // show a live countdown. Prefer an explicit start time from the event
  // metadata, else assume T+15 from the event's createdAt (backend fires this
  // event at T-15 minutes).
  let countdownToMs = null;
  if (eventType === "tournament_starting_soon" || eventType === "tournament_ending_soon") {
    const isEnding = eventType === "tournament_ending_soon";
    const explicitTarget = isEnding
      ? Number(eventData.metadata?.endTimeMs) ||
        Number(eventData.metadata?.endTime) ||
        null
      : Number(eventData.metadata?.startTimeMs) ||
        Number(eventData.metadata?.startTime) ||
        null;
    if (explicitTarget && Number.isFinite(explicitTarget)) {
      countdownToMs = explicitTarget;
    } else {
      const createdMs =
        typeof eventData.createdAt?.toMillis === "function"
          ? eventData.createdAt.toMillis()
          : typeof eventData.createdAt?.seconds === "number"
          ? eventData.createdAt.seconds * 1000
          : null;
      if (createdMs && Number.isFinite(createdMs)) {
        countdownToMs = createdMs + 15 * 60 * 1000;
      }
    }
  }

  return {
    eventType,
    title: String(eventData.title || template.title).trim() || template.title,
    message: customMessage || template.body(tournamentName, teamName),
    tone: template.tone,
    countdownToMs,
    navigateTo: eventData.metadata?.tournamentId
      ? { screen: "tournament", tournamentId: String(eventData.metadata.tournamentId).trim() }
      : null,
  };
}

export async function showTournamentNativeNotification({ title, message, tag }) {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return { shown: false, permission: "unsupported" };
  }

  let permission = Notification.permission;
  if (permission === "default") {
    try {
      permission = await Notification.requestPermission();
    } catch {
      permission = Notification.permission || "default";
    }
  }

  if (permission !== "granted") {
    return { shown: false, permission };
  }

  try {
    const notice = new Notification(title, {
      body: message,
      tag: tag || title,
      requireInteraction: false,
    });

    notice.onclick = () => {
      try {
        window.focus();
      } catch {}
      try {
        notice.close();
      } catch {}
    };

    return { shown: true, permission };
  } catch {
    return { shown: false, permission };
  }
}